import { generatePath } from 'react-router-dom';
import {
  isEmptyObject,
  omitUndefined,
  pick,
} from '@shared/helpers/object.helpers';
import {
  buildGenerationProjectPath,
  buildPartialProjectPath,
  buildSharedProjectPath,
  buildStrictProjectPath,
} from '@shared/helpers/router.helpers';
import { NODON_PROJECTS_LIST_PATH } from '@shared/constants/router.constants';
import {
  isFullGenerationProjectUrlParams,
  isFullProjectUrlParams,
  isFullSharedProjectUrlParams,
  SELECTED_ELEMENT_SEARCH_PARAM,
  URL_CATEGORY_NONE,
  type NodonGenerationProjectUrlParams,
  type NodonProjectFullUrlParams,
  type NodonSharedProjectUrlParams,
  type ProjectSelectionParams,
} from '@client/models/router.model';
import { Page } from '@client/store/ui/ui.model';
import { assertUnreachable } from '@shared/helpers/function.helpers';

/**
 * Patch applied on top of the existing URL query string (merge, not replace).
 * Keys not listed stay. `null` deletes a param.
 */
export type SearchParamsPatch = Record<string, string | null>;

type AuthenticatedProjectRouteData = Partial<
  Pick<NodonProjectFullUrlParams, 'organization' | 'projectId' | 'versionId'>
> &
  ProjectSelectionParams;

export type ProjectNavigationRoute =
  | { type: 'project'; data: AuthenticatedProjectRouteData }
  | { type: 'shared'; data: Partial<NodonSharedProjectUrlParams> }
  | { type: 'generation'; data: Partial<NodonGenerationProjectUrlParams> };

export type ResolveProjectNavigationInput = {
  route: ProjectNavigationRoute;
  navigationParams: Partial<NodonProjectFullUrlParams>;
  searchParams?: SearchParamsPatch;
};

export type ResolveProjectNavigationResult =
  | { action: 'navigate'; path: string; searchParams?: SearchParamsPatch }
  | { action: 'noop' }
  | {
      action: 'cancelled';
      reason: 'missing-organization' | 'missing-url-parameters';
    };

/**
 * Options passed from {@link resolveProjectNavigation} into the history navigate.
 * `flushSync` must be forwarded here or a following mutation can race the URL.
 */
export const innerNavigateOptionsFromProjectNavigate = (
  result: Extract<ResolveProjectNavigationResult, { action: 'navigate' }>,
  options: { flushSync?: boolean },
): { searchParams?: SearchParamsPatch; flushSync?: boolean } => ({
  searchParams: result.searchParams,
  flushSync: options.flushSync,
});

const selectedSearchPatch = (elementId: string): SearchParamsPatch => ({
  [SELECTED_ELEMENT_SEARCH_PARAM]:
    elementId === URL_CATEGORY_NONE ? null : elementId,
});

const nextSelectedElementId = (elementId: string | undefined): string =>
  elementId === undefined ? URL_CATEGORY_NONE : elementId;

const withSelectedPatch = (
  extraSearchParams: SearchParamsPatch | undefined,
  elementId: string,
): SearchParamsPatch => ({
  ...extraSearchParams,
  ...selectedSearchPatch(elementId),
});

/**
 * Next path + query patch for in-project navigation (authenticated, shared, or
 * generation). Omitted navigation fields keep their current route values.
 *
 * Scope query params (`lifecycles`, `sbefGroups`, …) are not rewritten here —
 * {@link SearchParamsPatch} is merged onto the current search string by the
 * caller, so they survive tab switches on public links.
 */
export const resolveProjectNavigation = ({
  route,
  navigationParams,
  searchParams: extraSearchParams,
}: ResolveProjectNavigationInput): ResolveProjectNavigationResult => {
  const params = omitUndefined(navigationParams);
  if (isEmptyObject(params)) return { action: 'noop' };

  switch (route.type) {
    case 'project':
      return resolveAuthenticatedProjectNavigation(
        route.data,
        params,
        extraSearchParams,
      );
    case 'shared': {
      const prevParams = route.data;
      if (!isFullSharedProjectUrlParams(prevParams)) {
        return { action: 'cancelled', reason: 'missing-url-parameters' };
      }
      return resolvePublicProjectNavigation({
        prevParams,
        buildPath: (versionId, page) =>
          buildSharedProjectPath({
            sharingKey: prevParams.sharingKey,
            versionId,
            page,
          }),
        navigationParams,
        extraSearchParams,
      });
    }
    case 'generation': {
      const prevParams = route.data;
      if (!isFullGenerationProjectUrlParams(prevParams)) {
        return { action: 'cancelled', reason: 'missing-url-parameters' };
      }
      return resolvePublicProjectNavigation({
        prevParams,
        buildPath: (versionId, page) =>
          buildGenerationProjectPath({
            generationKey: prevParams.generationKey,
            versionId,
            page,
          }),
        navigationParams,
        extraSearchParams,
      });
    }
    default:
      return assertUnreachable(route);
  }
};

const resolveAuthenticatedProjectNavigation = (
  prevParams: AuthenticatedProjectRouteData,
  params: Partial<NodonProjectFullUrlParams>,
  extraSearchParams: SearchParamsPatch | undefined,
): ResolveProjectNavigationResult => {
  const { organization, projectId, versionId, page, elementId } = params;
  const listSearchParams = extraSearchParams
    ? { searchParams: extraSearchParams }
    : {};

  if (
    (organization && organization !== prevParams.organization) ||
    (Object.keys(params).length === 1 && organization)
  ) {
    return {
      action: 'navigate',
      path: generatePath(NODON_PROJECTS_LIST_PATH, { organization }),
      ...listSearchParams,
    };
  }

  if (projectId && projectId !== prevParams.projectId) {
    const nextOrganization = organization ?? prevParams.organization;
    if (!nextOrganization) {
      return { action: 'cancelled', reason: 'missing-organization' };
    }
    if (versionId) {
      return {
        action: 'navigate',
        path: buildStrictProjectPath({
          organization: nextOrganization,
          projectId,
          versionId,
          page: page ?? Page.Design,
        }),
        searchParams: withSelectedPatch(
          extraSearchParams,
          nextSelectedElementId(elementId),
        ),
      };
    }
    return {
      action: 'navigate',
      path: buildPartialProjectPath({
        organization: nextOrganization,
        projectId,
      }),
      ...listSearchParams,
    };
  }

  if (!versionId && !page && !elementId) return { action: 'noop' };

  if (!isFullProjectUrlParams(prevParams)) {
    return { action: 'cancelled', reason: 'missing-url-parameters' };
  }

  return {
    action: 'navigate',
    path: buildStrictProjectPath({
      organization: prevParams.organization,
      projectId: prevParams.projectId,
      versionId: versionId ?? prevParams.versionId,
      page: page ?? prevParams.page,
    }),
    searchParams: withSelectedPatch(
      extraSearchParams,
      nextSelectedElementId(elementId),
    ),
  };
};

/** Shared and generation only differ by path template and identity key. */
const resolvePublicProjectNavigation = ({
  prevParams,
  buildPath,
  navigationParams,
  extraSearchParams,
}: {
  prevParams: { versionId: string; page: Page; elementId: string };
  buildPath: (versionId: string, page: string) => string;
  navigationParams: Partial<NodonProjectFullUrlParams>;
  extraSearchParams: SearchParamsPatch | undefined;
}): ResolveProjectNavigationResult => {
  const { versionId, page, elementId } = pick(
    navigationParams,
    'versionId',
    'page',
    'elementId',
  );
  if (!versionId && !page && !elementId) return { action: 'noop' };

  return {
    action: 'navigate',
    path: buildPath(versionId ?? prevParams.versionId, page ?? prevParams.page),
    searchParams: withSelectedPatch(
      extraSearchParams,
      nextSelectedElementId(elementId),
    ),
  };
};
